// The Hebrew or Greek behind the verse, and what Strong's says about a word.
//
// The words are laid out in *English* reading order, because that is the order
// the reader just read them in. They come from the Berean interlinear and are
// aligned to the Berean Standard Bible, not to the ESV — a licensed text is not
// ours to take apart word by word, and pretending otherwise would put an ESV
// word against a lemma it may not render. The card says so.
import { useState } from "react";
import { Icon } from "@/components/Icon";
import { track } from "@/lib/events";
import { useStrongs, useVerseWords } from "@/lib/study";
import { RailCard } from "./RailCard";

export function WordStudyCard({ verse }: { verse: string | null }) {
  const [open, setOpen] = useState<string | null>(null);
  const { data, isLoading } = useVerseWords(verse ?? undefined);
  const words = (data?.words ?? []).filter((w) => w.original);

  return (
    <RailCard title="Word study" verse={data?.display}>
      {!verse && <Empty>Click a verse to see the words behind it.</Empty>}
      {verse && isLoading && <Empty>Looking…</Empty>}
      {verse && data && !words.length && <Empty>No interlinear for this verse.</Empty>}

      {open ? (
        <StrongsPanel id={open} onBack={() => setOpen(null)} />
      ) : (
        words.length > 0 && (
          <>
            <div className="flex flex-wrap gap-1.5">
              {words.map((word) => (
                <button
                  key={word.position}
                  onClick={() => {
                    if (!word.strongs) return;
                    // The Strong's number first: the page counts words by it.
                    track({ type: "study_word", ref: verse ?? undefined, detail: `${word.strongs} ${word.transliteration || word.text}` });
                    setOpen(word.strongs);
                  }}
                  disabled={!word.strongs}
                  title={word.parsing || undefined}
                  className="rounded border border-rule px-1.5 py-1 text-left leading-tight hover:border-accent hover:bg-paper-2 disabled:cursor-default disabled:opacity-60 disabled:hover:border-rule disabled:hover:bg-transparent"
                >
                  <span className="block font-sans text-[11.5px] text-ink">{word.text || "—"}</span>
                  <span className="block font-serif text-[11px] text-ink-3 italic">{word.transliteration}</span>
                </button>
              ))}
            </div>
            <p className="mt-3 font-sans text-[10.5px] leading-normal text-ink-3">
              Words are aligned to the Berean Standard Bible, so they sit under the sense of the verse rather than
              under each ESV word.
            </p>
          </>
        )
      )}
    </RailCard>
  );
}

function StrongsPanel({ id, onBack }: { id: string; onBack: () => void }) {
  const { data, isLoading, error } = useStrongs(id);
  return (
    <div>
      <button onClick={onBack} className="mb-2 flex items-center gap-1 font-sans text-[11.5px] text-ink-3 hover:text-accent">
        <Icon name="chevron" width="12" height="12" className="rotate-180" />
        All the words
      </button>
      {isLoading && <Empty>Looking up {id}…</Empty>}
      {error && <Empty>Couldn’t load {id}.</Empty>}
      {data && (
        <div className="rounded-lg bg-paper-2/60 px-3.5 py-3">
          <p className="font-serif text-[19px] leading-tight text-ink">{data.lemma}</p>
          <p className="font-serif text-[13px] text-ink-2 italic">
            {data.transliteration}
            {data.pronunciation && <span className="text-ink-3"> · {data.pronunciation}</span>}
          </p>
          <p className="mt-1 font-sans text-[10.5px] tracking-[0.06em] text-ink-3 uppercase">
            {id} · {data.language} · {data.occurrences.toLocaleString()} uses
          </p>

          {data.gloss && <p className="mt-2.5 font-serif text-[14px] leading-snug text-ink">{data.gloss}</p>}
          {data.definition && <p className="mt-2 font-serif text-[13.5px] leading-snug text-ink-2">{data.definition}</p>}
          {data.derivation && <p className="mt-2 font-serif text-[13px] leading-snug text-ink-3 italic">{data.derivation}</p>}

          {data.renderings.length > 0 && (
            <div className="mt-3 border-t border-rule pt-2.5">
              <p className="font-sans text-[10.5px] tracking-[0.08em] text-ink-3 uppercase">Rendered as</p>
              <p className="mt-1 font-serif text-[13.5px] leading-snug text-ink-2">
                {data.renderings.map((r) => `${r.text} (${r.count})`).join(", ")}
              </p>
            </div>
          )}

          {data.samples.length > 0 && (
            <div className="mt-3 border-t border-rule pt-2.5">
              <p className="font-sans text-[10.5px] tracking-[0.08em] text-ink-3 uppercase">First uses</p>
              <ul className="mt-1 space-y-2">
                {data.samples.map((sample) => (
                  <li key={sample.ref}>
                    <span className="font-sans text-[11px] text-ink-3">{sample.display}</span>
                    <span className="mt-0.5 block font-serif text-[13.5px] leading-snug text-ink-2">{sample.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <p className="font-serif text-[14.5px] leading-relaxed text-ink-3 italic">{children}</p>;
}
